import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"

import { cn } from "@/lib/utils"

/*
  Pager — ‹ 1 … 4 5 6 … 15 › for paged tables. Hidden when there is one page.

    <Pager page={page} pageCount={pages} onPage={setPage} />

  variant 'outline' (bordered cells, soft navy current page — Inventory, Procurement, Forecast) |
  'solid' (borderless cells, filled navy current page — Shop Drawings). label = the nav's name.
  size 'default' | 'lg' (44px-tall cells, 40px wide — phone tap targets) | 'responsive' (lg below 640px only).
*/

/** Page numbers to show: always first / last / current ±1, 'gap-N' markers where pages are skipped. */
export function pageWindow(page, pageCount) {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1)
  const pages = new Set([1, pageCount, page, page - 1, page + 1])
  if (page <= 3) [2, 3, 4].forEach((value) => pages.add(value))
  if (page >= pageCount - 2) [pageCount - 1, pageCount - 2, pageCount - 3].forEach((value) => pages.add(value))
  const sorted = [...pages].filter((value) => value >= 1 && value <= pageCount).sort((a, b) => a - b)
  const out = []
  sorted.forEach((value, index) => {
    if (index > 0 && value - sorted[index - 1] > 1) out.push(`gap-${value}`)
    out.push(value)
  })
  return out
}

const VARIANTS = {
  outline: {
    nav: "gap-1",
    cell: "grid h-[26px] min-w-[26px] place-items-center rounded-[7px] border border-line bg-surface px-1.5 text-[12px] font-semibold text-ink-2 hover:bg-surface-hover disabled:pointer-events-none disabled:opacity-40",
    current: "border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink",
    icon: "h-3.5 w-3.5",
  },
  solid: {
    nav: "gap-0.5",
    cell: "grid h-7 min-w-[28px] place-items-center rounded-lg px-1.5 text-[12.5px] font-semibold text-ink-2 hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40",
    current: "bg-brand text-brand-fg hover:bg-brand-hover",
    icon: "h-[15px] w-[15px]",
  },
}

const SIZES = {
  default: "",
  lg: "h-11 min-w-[40px] text-[13px]",
  responsive: "max-sm:h-11 max-sm:min-w-[40px] max-sm:text-[13px]",
}

function Pager({ page, pageCount, onPage, label = "Pages", variant = "outline", size = "default", className }) {
  if (pageCount <= 1) return null
  const base = VARIANTS[variant] || VARIANTS.outline
  const style = { ...base, cell: cn(base.cell, SIZES[size] || "") }
  return (
    <nav aria-label={label} className={cn("flex items-center", style.nav, className)}>
      <button type="button" className={style.cell} onClick={() => onPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
        <ChevronLeft className={style.icon} aria-hidden="true" />
      </button>
      {pageWindow(page, pageCount).map((entry) => (
        typeof entry === "string"
          ? <span key={entry} className="px-1 text-[12px] text-ink-4" aria-hidden="true">…</span>
          : (
            <button
              key={entry}
              type="button"
              onClick={() => onPage(entry)}
              aria-current={entry === page ? "page" : undefined}
              aria-label={`Page ${entry}`}
              className={cn(style.cell, entry === page && style.current)}
            >
              {entry}
            </button>
          )
      ))}
      <button type="button" className={style.cell} onClick={() => onPage(page + 1)} disabled={page >= pageCount} aria-label="Next page">
        <ChevronRight className={style.icon} aria-hidden="true" />
      </button>
    </nav>
  )
}

export { Pager }
